import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  dismissSetupChecklistItem,
  getSetupChecklist,
  restoreSetupChecklistItem,
  type SetupChecklistResponse,
} from './api'

const KEY = ['setup-checklist']

/**
 * Refetch the checklist after the owner does one of the things it counts
 * (#700). Completion is derived on every read, so the server already knows
 * the profile is complete the moment the Settings card autosaves — but the
 * sidebar card is a separate query and would sit unticked until focus
 * returned or the page reloaded. Called from the mutations that close a gap:
 * the account-profile save, the Stripe connect return, a catalog add.
 */
export function invalidateSetupChecklist(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: KEY })
}

/**
 * The checklist behind the sidebar card.
 *
 * `staleTime: 0` on purpose. The server recomputes completion from live account
 * data on every read, so a refetch is how the card notices the owner just
 * connected Stripe in another tab — caching it would reintroduce exactly the
 * staleness recompute-on-read exists to prevent. It is a four-row query.
 *
 * While anything is still open it also polls. The event-driven refetches
 * above cover every write the portal itself makes, but the payments row flips
 * on Stripe's side — `charges_enabled` can arrive by webhook after the owner
 * has already left the onboarding — and nothing in the portal fires for that.
 * The poll stops the moment the list is complete, so a finished account pays
 * nothing.
 */
export const SETUP_CHECKLIST_POLL_MS = 15_000

export function useSetupChecklist(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    queryFn: getSetupChecklist,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (query.state.data?.complete ? false : SETUP_CHECKLIST_POLL_MS),
  })
}

/**
 * Dismiss and restore both return the whole reconciled list, so the cache is
 * seeded from the response rather than invalidated — one round trip, and the
 * card cannot flash a stale row between the mutation and a refetch.
 */
export function useDismissSetupItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => dismissSetupChecklistItem(key),
    onSuccess: (data: SetupChecklistResponse) => qc.setQueryData(KEY, data),
  })
}

export function useRestoreSetupItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (key: string) => restoreSetupChecklistItem(key),
    onSuccess: (data: SetupChecklistResponse) => qc.setQueryData(KEY, data),
  })
}
